import { useCallback, useMemo, useRef, useState } from 'react';
import { Sentry } from '@idk-web/core-utils';
import { useIsMounted } from '@idk-web/core-ui';
import { PrivateAppLoginStatusResponse } from '@idk-web/api';
import { isBankIDError } from '@/utils/error';
import {
  login as loginPrivate,
  logout as logoutPrivate,
} from '@/state/privateAuth';
import { StartedPrivateLoginRequest } from '@/hooks/private/login/privateLogin';
import * as privateLogin from '@/hooks/private/login/privateLogin';

type State =
  | {
      state: 'IDLE';
    }
  | {
      state: 'CANCELLED';
    }
  | {
      state: 'TIMED_OUT';
    }
  | {
      state: 'WAITING';
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      state: 'COMPLETE';
    }
  | {
      state: 'FAILED';
      error: unknown;
    };

export type PrivateLogin = {
  loginBankIdSe(): Promise<void>;
  loginFreja(): Promise<void>;
  loginBankIdNo(window: Window): Promise<void>;
  loginMitId(window: Window): Promise<void>;
  loginFtn(window: Window): Promise<void>;
  logout(): Promise<void>;
  cancel(): Promise<void>;
  reset(): void;
} & State;

export function usePrivateLogin(): PrivateLogin {
  const isMounted = useIsMounted();
  const request = useRef<StartedPrivateLoginRequest>(undefined);
  const [state, setState] = useState<State>({ state: 'IDLE' });
  const onInit = useCallback(
    async (
      data: Extract<PrivateAppLoginStatusResponse, { status: 'IN_PROGRESS' }>,
    ) => onProgress(data),
    [],
  );
  const onComplete = useCallback(
    async (
      result: Extract<PrivateAppLoginStatusResponse, { status: 'COMPLETED' }>,
    ) => {
      loginPrivate(result);

      if (isMounted()) {
        setState({ state: 'COMPLETE' });
      }

      request.current = undefined;
    },
    [],
  );
  const onError = useCallback(async (error: unknown) => {
    if (isMounted()) {
      if (error === 'cancelled') {
        setState({ state: 'CANCELLED' });
      } else if (error === 'timed out') {
        setState({ state: 'TIMED_OUT' });
      } else {
        if (!isBankIDError(error)) {
          Sentry.reportError('Private login failed', { error });
        }

        setState({ state: 'FAILED', error });
      }
    }

    request.current = undefined;
  }, []);
  const onProgress = useCallback(
    async (
      data: Extract<PrivateAppLoginStatusResponse, { status: 'IN_PROGRESS' }>,
    ) => {
      setState((oldState) => {
        let newState: State;
        if (data.bankId) {
          newState = { state: 'WAITING', bankId: data.bankId };
        } else if (data.freja) {
          newState = { state: 'WAITING', freja: data.freja };
        } else {
          newState = { state: 'WAITING' };
        }

        if (JSON.stringify(oldState) === JSON.stringify(newState)) {
          return oldState;
        } else {
          return newState;
        }
      });
    },
    [],
  );
  const loginBankIdSe = useCallback(async (): Promise<void> => {
    if (request.current) {
      return;
    }

    setState({ state: 'WAITING' });

    request.current = privateLogin
      .bankIdSe()
      .onInit(onInit)
      .onComplete(onComplete)
      .onError(onError)
      .onProgress(onProgress)
      .start();

    return request.current.wait();
  }, [onInit, onComplete, onError, onProgress]);
  const loginFreja = useCallback(async (): Promise<void> => {
    if (request.current) {
      return;
    }

    setState({ state: 'WAITING' });

    request.current = privateLogin
      .freja()
      .onInit(onInit)
      .onComplete(onComplete)
      .onError(onError)
      .onProgress(onProgress)
      .start();

    return request.current.wait();
  }, [onInit, onComplete, onError, onProgress]);
  const loginBankIdNo = useCallback(
    async (window: Window): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = privateLogin
        .bankIdNo(window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const loginMitId = useCallback(
    async (window: Window): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = privateLogin
        .mitId(window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const loginFtn = useCallback(
    async (window: Window): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = privateLogin
        .ftn(window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const logout = useCallback(async (): Promise<void> => {
    logoutPrivate();
    reset();
  }, []);
  const cancel = useCallback(async () => {
    if (isMounted()) {
      setState({ state: 'CANCELLED' });
    }

    return request.current?.cancel();
  }, []);
  const reset = useCallback(() => {
    if (isMounted()) {
      setState({ state: 'IDLE' });
    }

    request.current = undefined;
  }, []);

  return useMemo(
    () => ({
      loginBankIdSe,
      loginFreja,
      loginBankIdNo,
      loginMitId,
      loginFtn,
      logout,
      cancel,
      reset,
      ...state,
    }),
    [
      loginBankIdSe,
      loginBankIdNo,
      loginMitId,
      loginFtn,
      logout,
      cancel,
      reset,
      state,
    ],
  );
}
